import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Skeleton } from '@mui/material';
import styled from '@emotion/styled';
import { BaseOverflowBadgeProps, formatListForTooltip } from '@/components/_common/ListOverflowBadge/ListOverflowBadge';
import ConditionalTooltip from '@/components/_common/ConditionalTooltip/ConditionalTooltip';
import HiddenItemsCount, { MAX_WIDTH as HIDDEN_ITEMS_COUNT_MAX_WIDTH } from '@/components/_common/ListOverflowBadge/HiddenItemsCount';
import useIsElementResizing from '@/components/_hooks/useIsElementResizing';
import useCurrentBreakpoint from '@/components/_hooks/useCurrentBreakpoint';

export type ReactiveBadgeProps = BaseOverflowBadgeProps & { maxWidth?: number };

interface Sizes {
  availableWidth: number | null;
  listHeight: number | null;
  itemWidths: number[] | null;
}

/**
 * The component is a responsive badge display that shows as many items from a given list as can fit within the available horizontal space,
 * and hides the rest behind a "+N" counter with an optional tooltip.
 */
const ReactiveBadge: React.FC<ReactiveBadgeProps> = ({ list, maxWidth, delineated = false, tooltipDisplay }) => {
  const currentBreakpoint = useCurrentBreakpoint();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [sizes, setSizes] = useState<Sizes>({
    availableWidth: null,
    listHeight: null,
    itemWidths: null
  });
  const [isReadyToShow, setIsReadyToShow] = useState(false);

  const { isResizing } = useIsElementResizing({
    ref: wrapperRef,
    trackedDimensions: 'width'
  });

  useEffect(() => {
    if (isResizing) {
      if (isReadyToShow) setIsReadyToShow(false);
    } else {
      if (!wrapperRef.current || !listRef.current) return;

      const availableWidth = Math.floor(wrapperRef.current.getBoundingClientRect().width);
      const listHeight = listRef.current.getBoundingClientRect().height;
      const itemWidths = Array.from(listRef.current.children).map((child) => child.getBoundingClientRect().width);

      setSizes({
        availableWidth,
        listHeight,
        itemWidths
      });
      setIsReadyToShow(true);
    }
  }, [isResizing, isReadyToShow, currentBreakpoint]);

  const visibleItemsCount = useMemo(() => {
    const { availableWidth, itemWidths } = sizes;

    if (availableWidth === null || itemWidths === null) {
      return 0;
    }

    let count = 0;
    let pixelsLeft = availableWidth - HIDDEN_ITEMS_COUNT_MAX_WIDTH; // reserve place for the <HiddenItemsCount /> component, by subtracting its max width

    for (let i = 0; i < itemWidths.length; i++) {
      pixelsLeft -= itemWidths[i];
      if (pixelsLeft <= 0) {
        break;
      }
      count++;
    }

    return count;
  }, [sizes]);

  const getDelimiter = (index: number) => {
    if (visibleItemsCount && visibleItemsCount < list.length) {
      return index < visibleItemsCount - 1 ? ',' : '';
    } else {
      return index < list.length - 1 ? ',' : '';
    }
  };

  const hiddenItemsCount = list.length - visibleItemsCount;

  return (
    <Wrapper ref={wrapperRef} maxWidth={maxWidth} height={sizes.listHeight}>
      <SkeletonWrapper visible={isResizing}>
        {Array.from({ length: Math.min(list.length, 3) }).map((_, i) => (
          <ChipSkeleton key={i} variant="rectangular" height={sizes.listHeight ?? 20} />
        ))}
        <BadgeSkeleton variant="circular" />
      </SkeletonWrapper>
      <ItemsList visible={isReadyToShow} ref={listRef}>
        {list.map((item, index) => (
          <Item key={index} isVisible={index + 1 <= visibleItemsCount}>
            {item}
            {delineated && getDelimiter(index)}
          </Item>
        ))}
        {hiddenItemsCount > 0 && (
          <ConditionalTooltip show={!!tooltipDisplay} title={formatListForTooltip(tooltipDisplay, list.slice(visibleItemsCount ?? 0))}>
            <HiddenItemsCount>+{list.length - visibleItemsCount}</HiddenItemsCount>
          </ConditionalTooltip>
        )}
      </ItemsList>
    </Wrapper>
  );
};

export default ReactiveBadge;

const Wrapper = styled.div<{ maxWidth?: number; height: number | null }>`
  display: flex;
  width: 100%;
  position: relative; // required to make sure that "overflow: hidden;" will clip <ItemsList /> which is positioned absolutely. Otherwise <ItemsList /> element might stick outside <Wrapper /> even though <Wrapper /> has "overflow: hidden;"
  overflow: hidden; // when browser window is resized visibleItemsCount is calculated asynchronously, and therefore items might stick outside <Wrapper /> before visibleItemsCount recalculated and proper number of items rendered.
  max-width: ${({ maxWidth }) => (maxWidth !== undefined ? `${maxWidth}px` : 'initial')};
  height: ${({ height }) => (height !== null ? `${height}px` : 'initial')};
`;

const SkeletonWrapper = styled.div<{ visible: boolean }>`
  position: absolute;
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  column-gap: 10px;
`;

const ChipSkeleton = styled(Skeleton)<{ height: number }>`
  width: 100px;
  height: ${({ height }) => height}px;
  padding: ${({ theme }) => theme.spacing(0.5)};
  border-radius: 4px;
  display: inline-block;
`;

const BadgeSkeleton = styled(Skeleton)`
  width: 20px;
  height: 20px;
`;

const ItemsList = styled.div<{ visible: boolean }>`
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  position: absolute;
  display: flex;
  align-items: center;
`;

const Item = styled.div<{ isVisible: boolean }>`
  white-space: nowrap;
  padding-right: 5px;

  ${({ isVisible }) => {
    if (!isVisible) {
      return `
        position: absolute;
        pointer-events: none;
        opacity: 0;
      `;
    }
  }}
`;
